import * as yup from 'yup';
import { useLazyQuery } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers';
import { SubmitHandler, useForm } from 'react-hook-form';
// Api
import { GET_PROMO_CODE } from 'api/promoCodes/queries';
// Types
import {
  GetPromocode,
  GetPromocodeVariables,
} from 'api/promoCodes/types/GetPromocode';
// Hooks
import { useGetCurrUser } from 'hooks';
// Helpers
import { yupStringRequiredValidation } from 'helpers/validation';
// Ui
import Modal from 'ui3/Modal/Modal';
import TextField from 'ui3/TextField/TextField';
import Button from 'ui3/Button/Button';
// Components
import { showToast } from 'components/common/Toast/Toast';
// Styles
import styles from './ActivatePromoCodeModal.module.scss';

export type ActivatePromoCodeModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (promoCode: GetPromocode['getPromocode']) => void;
  amaId?: string;
  streamId?: string;
  merchIds?: number[];
};

type FormInputs = {
  promoCodeName: string;
};

const validationSchema = yup.object().shape({
  promoCodeName: yupStringRequiredValidation('Promo code'),
});

const ActivatePromoCodeModal = ({
  amaId,
  streamId,
  merchIds,
  isOpen,
  onClose,
  onSuccess,
}: ActivatePromoCodeModalProps) => {
  const { data: user } = useGetCurrUser();

  const {
    register,
    handleSubmit,
    errors: formErrors,
    setError,
    clearErrors,
  } = useForm<FormInputs>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      promoCodeName: '',
    },
  });

  const [getPromocode, { loading, error }] = useLazyQuery<
    GetPromocode,
    GetPromocodeVariables
  >(GET_PROMO_CODE, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onError: () => {
      if (error?.message) {
        setError('promoCodeName', { message: error.message });
      }
    },
    onCompleted: (data) => {
      if (data?.getPromocode) {
        showToast({
          type: 'success',
          message: "You've successfully activated Promo code",
        });

        onSuccess(data.getPromocode);
      }
    },
  });

  const handleCloseModal = () => {
    if (onClose) {
      onClose();
    }

    clearErrors();
  };

  const handleFormSubmit: SubmitHandler<FormInputs> = async (values) => {
    try {
      getPromocode({
        variables: {
          input: {
            amaId,
            streamId,
            merchIds,
            name: values.promoCodeName.trim(),
            customerId: user?.me?.id,
          },
        },
      });
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Modal
      title="Promo code"
      size="small"
      open={isOpen}
      onClose={handleCloseModal}
      focusTrapped={false}
    >
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <div className={styles.formItem}>
          <TextField
            name="promoCodeName"
            ref={register}
            placeholder="Enter code here..."
            error={formErrors?.promoCodeName?.message}
            label="Promo code"
            disabled={loading}
          />
        </div>

        <div className={styles.buttonWrapper}>
          <Button type="submit" loading={loading}>
            Activate
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default ActivatePromoCodeModal;
